import { useEffect, useState } from 'react';

import * as dashboardApi from '../../api/dashboardApi';
import { fetchPlans, type Plan } from '../../api/billingApi';
import { isBloc } from './WorldDots';

/**
 * Everything the public marketing pages say about the corpus, read live.
 *
 * Extracted when the second page arrived: the two would otherwise each carry
 * their own copy of this, and the figures on them would drift apart the first
 * time one was changed. Every page that makes a claim about the corpus now
 * makes it from the same source.
 *
 * Nothing here throws. Each request fails on its own and takes only its own
 * section with it — a page is worth showing without its figures, and reads
 * "—" until they arrive.
 */

export interface LiveStats {
  regulations: number;
  jurisdictions: number;
  countries: number;
}

export interface CorpusFacts {
  stats: LiveStats | null;
  /** The busiest jurisdictions, largest first. */
  top: { name: string; count: number }[];
  /** Every jurisdiction code, busiest first. */
  codes: string[];
  byCountry: Record<string, number>;
  /** Codes in the corpus that ISO 3166 does not know as countries. */
  blocs: string[];
  trend: dashboardApi.TrendDataPoint[];
  plans: Plan[];
}

/** What a page actually reads. "/home2" shows only the three headline
 *  figures, so asking for the growth series and the plan strip there would be
 *  two round trips whose results are thrown away. Both default to on, so the
 *  pages that do use them are unchanged. */
export interface CorpusFactsOptions {
  growth?: boolean;
  pricing?: boolean;
}

export function useCorpusFacts(
  documentTitle?: string,
  options?: CorpusFactsOptions,
): CorpusFacts {
  const wantGrowth = options?.growth !== false;
  const wantPricing = options?.pricing !== false;
  const [stats, setStats] = useState<LiveStats | null>(null);
  const [top, setTop] = useState<{ name: string; count: number }[]>([]);
  const [codes, setCodes] = useState<string[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [byCountry, setByCountry] = useState<Record<string, number>>({});
  const [blocs, setBlocs] = useState<string[]>([]);
  const [trend, setTrend] = useState<dashboardApi.TrendDataPoint[]>([]);

  useEffect(() => {
    if (documentTitle) document.title = documentTitle;
    let cancelled = false;

    /* Three requests, none of which needs any of the others, so they go out
       together. Awaited in sequence the plan strip could not paint until the
       figures and the trend series had both come back — round trips deep on a
       page whose whole job is to land quickly. */
    const figures = async () => {
      try {
        const [jurisData, statsData] = await Promise.all([
          dashboardApi.getTotalJurisdictions(),
          dashboardApi.getJurisdictionStats({}),
        ]);
        if (cancelled) return;
        const rows = statsData?.stats ?? [];
        const perCountry = statsData?.by_country ?? {};
        setStats({
          regulations: rows.reduce((sum, s) => sum + s.regulation_count, 0),
          jurisdictions: jurisData?.total ?? 0,
          countries: Object.keys(perCountry).filter((k) => !isBloc(k)).length,
        });
        const busiest = [...rows].sort((a, b) => b.regulation_count - a.regulation_count);
        setTop(busiest.map((s) => ({ name: s.jurisdiction_name, count: s.regulation_count })));
        setCodes(busiest.map((s) => s.jurisdiction_code).filter((c): c is string => Boolean(c)));
        setByCountry(perCountry);
        setBlocs(Object.keys(perCountry).filter(isBloc));
      } catch {
        // The page is worth showing without the live figures.
      }
    };

    const growth = async () => {
      try {
        const t = await dashboardApi.getTrends({ granularity: 'month' });
        if (cancelled) return;
        setTrend(t?.data ?? []);
      } catch {
        // The chart is evidence for the claim, not the claim.
      }
    };

    // Prices come from the admin catalogue, so a marketing page cannot drift
    // from the pricing page the way a hard-coded strip would.
    const pricing = async () => {
      try {
        const list = await fetchPlans();
        if (cancelled) return;
        setPlans((list ?? [])
          .filter((pl) => pl.show_on_pricing !== false && !pl.disabled)
          .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
          .slice(0, 4));
      } catch {
        // The strip is a teaser for /pricing, not the page's subject.
      }
    };

    void Promise.all([
      figures(),
      ...(wantGrowth ? [growth()] : []),
      ...(wantPricing ? [pricing()] : []),
    ]);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { stats, top, codes, byCountry, blocs, trend, plans };
}

/** "1 jurisdiction", "5 jurisdictions", "Unlimited jurisdictions".
 *
 *  A quantity in the catalogue is either a number or a word, and the word
 *  arrives lower-cased, so both the plural and the capital are decided here.
 */
export const quantity = (
  raw: number | string | null | undefined,
  one: string,
  many: string,
): string => {
  if (raw === null || raw === undefined || raw === '') return `— ${many}`;
  const n = Number(raw);
  if (Number.isFinite(n)) return `${n.toLocaleString()} ${n === 1 ? one : many}`;
  const word = String(raw);
  return `${word.charAt(0).toUpperCase()}${word.slice(1)} ${many}`;
};

/** A figure, or an em dash while it is still loading. */
export const fmt = (n: number | null | undefined) =>
  (n == null ? '—' : n.toLocaleString());
